"use client";

import React from "react";
import {
  RowContextMenuContentView,
  type RowContextMenuContentViewProps,
} from "./RowContextMenuContentView";
import { useActionFiltering } from "@/lib/hooks/search/useActionFiltering";
import type { BaseEntity } from "@/components/shared/UniversalDetailPage/types/detailPage";

export type { ActiveCell } from "./RowContextMenuContentView";

export type RowContextMenuContentProps<T extends BaseEntity> = Omit<
  RowContextMenuContentViewProps<T>,
  "updateMutation" | "deleteMutation" | "filterActionsByRole"
>;

/**
 * Standalone right-click menu: subscribes to the entity's update + delete
 * mutations and to the action role filter itself. Use from surfaces OUTSIDE a
 * `SimpleTable` (cards, drilldowns, detail-page panels — see
 * ENTITY_TABLE_ROW_ACTIONS).
 *
 * Inside a `SimpleTable`, `TableRow` renders `RowContextMenuContentView`
 * directly with the table's shared mutation pair.
 */
export function RowContextMenuContent<T extends BaseEntity>(
  props: RowContextMenuContentProps<T>
) {
  const updateMutation = props.hooks.useUpdate();
  const deleteMutation = props.hooks.useDelete();
  const { filterActionsByRole } = useActionFiltering();

  return (
    <RowContextMenuContentView<T>
      {...props}
      updateMutation={updateMutation}
      deleteMutation={deleteMutation}
      filterActionsByRole={filterActionsByRole}
    />
  );
}
